import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Typography,
  Space,
  Spin,
  Result,
  Tag,
  Divider,
  Empty,
  Tabs,
  TabsProps,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LinkedinOutlined,
  GithubOutlined,
  GlobalOutlined,
  BankOutlined,
  BookOutlined,
  TrophyOutlined,
  ProjectOutlined,
  SafetyCertificateOutlined,
  TranslationOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import {
  Userprofile,
  ResumeExperience,
  ResumeEducation,
  ResumeProject,
  ResumeCertification,
} from "../../Entities/user-profile.entity";
import "./ResumeViewerDrawer.scss";

const { Text, Title, Paragraph } = Typography;

interface ResumeViewerDrawerProps {
  open: boolean;
  onClose: () => void;
  profileId?: string;
}

const ResumeViewerDrawer: React.FC<ResumeViewerDrawerProps> = ({
  open,
  onClose,
  profileId,
}) => {
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [profile, setProfile] = useState<Userprofile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("pdf");

  const profileMatcherService = new ProfileMatcherService();

  const resumeUrl = profile
    ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${profile.userId}/${profile.filename}`
    : null;

  // Fetch profile data when drawer opens
  useEffect(() => {
    const fetchProfile = async () => {
      if (!open || !profileId) return;

      setIsFetchingProfile(true);
      setHasError(false);

      try {
        const profiles = await profileMatcherService.getAllUploadedProfiles();
        const matchedProfile = profiles?.find(
          (p: Userprofile) => p._id === profileId,
        );
        setProfile(matchedProfile || null);
      } catch (error) {
        console.error("Error fetching profile:", error);
        setHasError(true);
      } finally {
        setIsFetchingProfile(false);
      }
    };

    fetchProfile();
  }, [open, profileId]);

  const handleDownload = () => {
    if (resumeUrl) {
      window.open(resumeUrl, "_blank");
    }
  };

  // Format date helper
  const formatDate = (date: Date | string | undefined | null) => {
    if (!date) return "";
    const d = new Date(date as string);
    if (isNaN(d.getTime())) return String(date);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  // Check if profile has meaningful extracted data (not just empty objects)
  const hasExtractedData =
    profile &&
    ((profile.skills &&
      Array.isArray(profile.skills) &&
      profile.skills.length > 0) ||
      (profile.experience &&
        Array.isArray(profile.experience) &&
        profile.experience.length > 0) ||
      (profile.education &&
        Array.isArray(profile.education) &&
        profile.education.length > 0) ||
      (profile.basicDetails &&
        (profile.basicDetails.firstName ||
          profile.basicDetails.lastName ||
          profile.basicDetails.email ||
          profile.basicDetails.phoneNumber ||
          profile.basicDetails.address)) ||
      (profile.summary && profile.summary.trim().length > 0));


  // Reset loading state when resume URL changes
  useEffect(() => {
    if (resumeUrl) {
      setIsLoading(true);
    }
  }, [resumeUrl]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Helper function to format social media URLs
  const formatSocialUrl = (
    url: string | null | undefined,
    type: "linkedin" | "github" | "portfolio",
  ): string | null => {
    if (!url || !url.trim()) return null;

    const trimmedUrl = url.trim();

    // If it's already a full URL, return as is
    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      return trimmedUrl;
    }

    // If it contains a domain, add https://
    if (
      trimmedUrl.includes("linkedin.com") ||
      trimmedUrl.includes("github.com") ||
      trimmedUrl.includes(".")
    ) {
      return trimmedUrl.startsWith("//")
        ? `https:${trimmedUrl}`
        : `https://${trimmedUrl}`;
    }

    // If it's just a username, format it properly
    if (type === "linkedin") {
      // LinkedIn usernames can be /in/username or just username
      if (trimmedUrl.startsWith("/in/") || trimmedUrl.startsWith("in/")) {
        return `https://www.linkedin.com${trimmedUrl.startsWith("/") ? "" : "/"}${trimmedUrl}`;
      }
      return `https://www.linkedin.com/in/${trimmedUrl}`;
    }

    if (type === "github") {
      return `https://github.com/${trimmedUrl}`;
    }

    // For portfolio, if it doesn't have a protocol, add https://
    return `https://${trimmedUrl}`;
  };

  // Render basic details section
  const renderBasicDetails = () => {
    if (!profile?.basicDetails) return null;
    const { firstName, lastName, email, phoneNumber, address } =
      profile.basicDetails;
    const fullName = [firstName, lastName].filter(Boolean).join(" ");

    return (
      <div className="resume-section resume-section--header">
        <div className="profile-header">
          <div className="profile-avatar">
            <UserOutlined />
          </div>
          <div className="profile-info">
            {fullName && (
              <Title level={4} className="profile-name">
                {fullName}
              </Title>
            )}
            <div className="profile-contact">
              {email && (
                <span className="contact-item">
                  <MailOutlined /> {email}
                </span>
              )}
              {phoneNumber && (
                <span className="contact-item">
                  <PhoneOutlined /> {phoneNumber}
                </span>
              )}
              {address && (
                <span className="contact-item">
                  <EnvironmentOutlined /> {address}
                </span>
              )}
            </div>
          </div>
        </div>
        {renderSocialLinks()}
      </div>
    );
  };

  // Render social links - only show if they exist
  const renderSocialLinks = () => {
    if (!profile?.socialLinks) return null;
    const { linkedin, github, personalPortfolio } = profile.socialLinks;

    // Format URLs properly
    const formattedLinkedIn = formatSocialUrl(linkedin, "linkedin");
    const formattedGithub = formatSocialUrl(github, "github");
    const formattedPortfolio = formatSocialUrl(personalPortfolio, "portfolio");

    // Only render if at least one link exists
    if (!formattedLinkedIn && !formattedGithub && !formattedPortfolio)
      return null;

    return (
      <div className="social-links">
        {formattedLinkedIn && (
          <a
            href={formattedLinkedIn}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <LinkedinOutlined /> LinkedIn
          </a>
        )}
        {formattedGithub && (
          <a
            href={formattedGithub}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <GithubOutlined /> GitHub
          </a>
        )}
        {formattedPortfolio && (
          <a
            href={formattedPortfolio}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <GlobalOutlined /> Portfolio
          </a>
        )}
      </div>
    );
  };

  // Render summary section
  const renderSummary = () => {
    if (!profile?.summary) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <BulbOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Professional Summary
          </Title>
        </div>
        <Paragraph className="summary-text">{profile.summary}</Paragraph>
      </div>
    );
  };

  // Render experience section
  const renderExperience = () => {
    if (!profile?.experience || profile.experience.length === 0) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <BankOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Work Experience
          </Title>
        </div>
        <div className="timeline-list">
          {profile.experience.map((exp: ResumeExperience, index: number) => (
            <div key={index} className="timeline-item">
              <div className="timeline-header">
                <div className="timeline-title">
                  <Text strong>{exp.designation}</Text>
                  <Text type="secondary"> at {exp.companyName}</Text>
                </div>
                <Text type="secondary" className="timeline-date">
                  {formatDate(typeof exp.from === "string" ? exp.from : exp.from?.toISOString?.() ?? "")} -{" "}
                  {exp.to == null
                    ? "Present"
                    : formatDate(typeof exp.to === "string" ? exp.to : (exp.to as Date)?.toISOString?.() ?? "")}
                </Text>
              </div>
              {exp.location && (
                <Text type="secondary" className="timeline-location">
                  <EnvironmentOutlined /> {exp.location}
                </Text>
              )}
              {exp.description && (
                <Paragraph className="timeline-description">
                  {exp.description}
                </Paragraph>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render education section
  const renderEducation = () => {
    if (!profile?.education || profile.education.length === 0) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <BookOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Education
          </Title>
        </div>
        <div className="timeline-list">
          {profile.education.map((edu: ResumeEducation, index: number) => (
            <div key={index} className="timeline-item">
              <div className="timeline-header">
                <div className="timeline-title">
                  <Text strong>{edu.degree}</Text>
                </div>
                <Text type="secondary" className="timeline-date">
                  {formatDate(typeof edu.from === "string" ? edu.from : edu.from?.toISOString?.() ?? "")} -{" "}
                  {edu.to == null
                    ? "Present"
                    : formatDate(typeof edu.to === "string" ? edu.to : (edu.to as Date)?.toISOString?.() ?? "")}
                </Text>
              </div>
              <Text type="secondary">{edu.school}</Text>
              {edu.location && (
                <Text type="secondary" className="timeline-location">
                  <EnvironmentOutlined /> {edu.location}
                </Text>
              )}
              {edu.description && (
                <Paragraph className="timeline-description">
                  {edu.description}
                </Paragraph>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render skills section
  const renderSkills = () => {
    if (!profile?.skills || profile.skills.length === 0) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <TrophyOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Skills
          </Title>
        </div>
        <div className="tags-container">
          {profile.skills.map((skill: string, index: number) => (
            <Tag key={index} className="skill-tag">
              {skill}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  // Render projects section
  const renderProjects = () => {
    if (!profile?.projects || profile.projects.length === 0) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <ProjectOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Projects
          </Title>
        </div>
        <div className="projects-list">
          {profile.projects.map((project: ResumeProject, index: number) => (
            <div key={index} className="project-item">
              <div className="project-header">
                <Text strong>{project.title}</Text>
                <div className="project-links">
                  {project.githubLink && (
                    <a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GithubOutlined />
                    </a>
                  )}
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GlobalOutlined />
                    </a>
                  )}
                </div>
              </div>
              {project.description && (
                <Paragraph className="project-description">
                  {project.description}
                </Paragraph>
              )}
              {project.technologies && project.technologies.length > 0 && (
                <div className="project-tech">
                  {project.technologies.map((tech: string, i: number) => (
                    <Tag key={i} className="tech-tag">
                      {tech}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render certifications section
  const renderCertifications = () => {
    if (!profile?.certifications || profile.certifications.length === 0)
      return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <SafetyCertificateOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Certifications
          </Title>
        </div>
        <div className="certifications-list">
          {profile.certifications.map(
            (cert: ResumeCertification, index: number) => (
              <div key={index} className="certification-item">
                <Text strong>{cert.name}</Text>
                <Text type="secondary"> • {cert.issuer}</Text>
                {cert.year && <Text type="secondary"> • {cert.year}</Text>}
              </div>
            ),
          )}
        </div>
      </div>
    );
  };

  // Render achievements section
  const renderAchievements = () => {
    if (!profile?.achievements || profile.achievements.length === 0)
      return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <TrophyOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Achievements
          </Title>
        </div>
        <ul className="achievements-list">
          {profile.achievements.map((achievement: string, index: number) => (
            <li key={index}>{achievement}</li>
          ))}
        </ul>
      </div>
    );
  };

  // Render languages section
  const renderLanguages = () => {
    if (!profile?.languages || profile.languages.length === 0) return null;
    return (
      <div className="resume-section">
        <div className="section-header">
          <TranslationOutlined className="section-icon" />
          <Title level={5} className="section-title">
            Languages
          </Title>
        </div>
        <div className="tags-container">
          {profile.languages.map((lang: string, index: number) => (
            <Tag key={index} className="language-tag">
              {lang}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  // Render extracted content
  const renderExtractedContent = () => {
    if (isFetchingProfile) {
      return (
        <div className="resume-viewer-loading">
          <Spin size="large" tip="Loading your profile..." />
        </div>
      );
    }

    if (!profileId || !profile) {
      return (
        <Result
          icon={<FileTextOutlined style={{ color: "var(--color-warning)" }} />}
          title="No Profile Found"
          subTitle="Profile not available for this application."
        />
      );
    }

    if (hasError) {
      return (
        <Result
          status="error"
          title="Failed to Load Profile"
          subTitle="We couldn't load your profile. Please try again."
        />
      );
    }

    if (!hasExtractedData) {
      return (
        <div className="resume-viewer-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No extracted data available"
          />
          <Text type="secondary" className="empty-hint">
            The resume data hasn't been processed yet. You can download the
            original file.
          </Text>
          {resumeUrl && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              style={{ marginTop: 16 }}
            >
              Download Original Resume
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="resume-viewer-content">
        {renderBasicDetails()}
        <Divider />
        {renderSummary()}
        {renderExperience()}
        {renderEducation()}
        {renderSkills()}
        {renderProjects()}
        {renderCertifications()}
        {renderAchievements()}
        {renderLanguages()}
      </div>
    );
  };

  // Render PDF preview
  const renderPdfContent = () => {
    if (!resumeUrl) {
      return (
        <div className="resume-viewer-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No data"
          />
        </div>
      );
    }

    return (
      <div className="resume-preview-pdf-container">
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(8px)",
              zIndex: 1,
              borderRadius: "8px",
              gap: "16px",
            }}
          >
            <Spin size="large" />
            <Text
              style={{
                color: "rgba(0, 0, 0, 0.65)",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Loading resume...
            </Text>
          </div>
        )}
        <iframe
          src={resumeUrl}
          title="Resume Preview"
          onLoad={handleIframeLoad}
        />
      </div>
    );
  };

  // Tab items - only create when we have data
  const tabItems: TabsProps["items"] = [];

  if (resumeUrl) {
    tabItems.push({
      key: "pdf",
      label: "PDF Preview",
      children: renderPdfContent(),
    });
  }

  if (hasExtractedData) {
    tabItems.push({
      key: "extracted",
      label: "Extracted Content",
      children: renderExtractedContent(),
    });
  }
 

  // Determine what to render
  const renderDrawerContent = () => {
    // Only show tabs if we have BOTH extracted data AND PDF (2+ tabs)
    if (hasExtractedData && resumeUrl && tabItems.length > 1) {
      return (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="line"
          destroyInactiveTabPane={true}
        />
      );
    }

    // Show only extracted content if available (no PDF)
    if (hasExtractedData && !resumeUrl) {
      return renderExtractedContent();
    }

    // Show only PDF if available (no extracted content)
    if (resumeUrl && !hasExtractedData) {
      return renderPdfContent();
    }

    // Show empty state if neither exists
    return (
      <div className="resume-viewer-empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No profile data available"
        />
      </div>
    );
  };

  return (
    <Drawer
      title={
        <div className="resume-viewer-header">
          <Space>
            <FileTextOutlined />
            <Typography.Text strong>Your Profile</Typography.Text>
          </Space>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      className="resume-viewer-drawer"
      extra={
        profile &&
        resumeUrl && (
          <Button icon={<DownloadOutlined />} onClick={handleDownload}>
            Download PDF
          </Button>
        )
      }
    >
      {renderDrawerContent()}
    </Drawer>
  );
};

export default ResumeViewerDrawer;
